/**
 * ProfileCard — the single card component every surface renders (Phase A).
 *
 * Identity + active character + visibility-filtered fields + an action slot.
 * The action slot is driven by `viewerConnectionState`:
 *   - social.graph off  → no action button (card is useful stand-alone)
 *   - none              → Connect
 *   - pending_outbound  → Requested (cancel)
 *   - pending_inbound   → Accept / Decline
 *   - accepted          → Message (+ invites in Phase D)
 *   - blocked           → (no positive action)
 *
 * Visibility is filtered SERVER-SIDE — this component only renders the fields
 * it was given, never deciding entitlement itself.
 */

import { useEffect, useState } from 'react';
import { useProfileCards } from '../../context/ProfileCardContext';
import { useSocialGraph } from '../../context/SocialGraphContext';
import { useConnectionState } from '../../hooks/useConnectionState';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { PresenceBadge } from '../social/PresenceBadge';
import { InviteButton } from '../social/InviteButton';
import { getMyInterests } from '../../services/api/profileService';
import type { ConnectionState, ProfileCard as ProfileCardModel } from '../../types/social';
import './ProfileCard.css';

/** "You both love X & Y" — parses the card's comma-joined "interests" field
 * (V2 Amethyst WU-02, R3) and intersects it with the viewer's own interests.
 * Never fabricates: returns null when there's no field or no overlap. */
function sharedInterestLine(card: ProfileCardModel, viewerInterestNames: string[]): string | null {
  const raw = card.fields.find((f) => f.key === 'interests')?.value;
  if (!raw) return null;
  const cardNames = raw.split(',').map((s) => s.trim()).filter(Boolean);
  const shared = cardNames.filter((name) => viewerInterestNames.includes(name));
  if (shared.length === 0) return null;
  return `You both love ${shared.slice(0, 2).join(' & ')}`;
}

interface Props {
  profileId: string;
  /** Called with the connection id when the viewer opens a DM (Phase C). */
  onMessage?: (connectionId: string, card: ProfileCardModel) => void;
  onClose?: () => void;
}

export function ProfileCard({ profileId, onMessage, onClose }: Props) {
  const { getCard } = useProfileCards();
  const graphEnabled = useFeatureFlag('social.graph');
  const presenceEnabled = useFeatureFlag('social.dm_presence');
  const invitesEnabled = useFeatureFlag('social.invites_feed');
  const graph = useSocialGraph();
  const liveState = useConnectionState(profileId);

  const [card, setCard] = useState<ProfileCardModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [viewerInterestNames, setViewerInterestNames] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCard(profileId)
      .then((c) => {
        if (!cancelled) setCard(c);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId, getCard]);

  // Own interests, for the shared-interest lead line — fetched once per card
  // open, not per keystroke; cheap, and this component already re-mounts
  // per profileId via ProfileCardTrigger's portal.
  useEffect(() => {
    let cancelled = false;
    getMyInterests().then((interests) => {
      if (!cancelled) setViewerInterestNames(interests.map((i) => i.displayName));
    });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  if (loading) {
    return <div className="profile-card profile-card--loading">Loading…</div>;
  }
  if (!card) {
    return (
      <div className="profile-card profile-card--error">
        <p>Profile unavailable.</p>
        {onClose && (
          <button className="profile-card__close" onClick={onClose}>
            Close
          </button>
        )}
      </div>
    );
  }

  // Prefer the live graph state once loaded; fall back to the server's snapshot.
  const state: ConnectionState = graphEnabled ? liveState : card.viewerConnectionState;

  const pendingInboundRequest = graph.inbound.find(
    (r) => r.initiatorProfileId === profileId && r.status === 'pending'
  );
  const pendingOutboundRequest = graph.outbound.find(
    (r) => r.targetProfileId === profileId && r.status === 'pending'
  );
  const connection = graph.connections.find((c) => c.otherProfileId === profileId);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const renderAction = () => {
    if (!graphEnabled) return null;
    switch (state) {
      case 'none':
        return (
          <button
            className="profile-card__action profile-card__action--primary"
            disabled={busy}
            onClick={() => run(() => graph.sendRequest(profileId))}
          >
            Connect
          </button>
        );
      case 'pending_outbound':
        return (
          <button
            className="profile-card__action"
            disabled={busy || !pendingOutboundRequest}
            onClick={() =>
              pendingOutboundRequest && run(() => graph.cancel(pendingOutboundRequest.id))
            }
          >
            Requested
          </button>
        );
      case 'pending_inbound':
        return (
          <div className="profile-card__action-group">
            <button
              className="profile-card__action profile-card__action--primary"
              disabled={busy || !pendingInboundRequest}
              onClick={() =>
                pendingInboundRequest && run(() => graph.accept(pendingInboundRequest.id))
              }
            >
              Accept
            </button>
            <button
              className="profile-card__action"
              disabled={busy || !pendingInboundRequest}
              onClick={() =>
                pendingInboundRequest && run(() => graph.decline(pendingInboundRequest.id))
              }
            >
              Decline
            </button>
          </div>
        );
      case 'accepted':
        return (
          <div className="profile-card__action-group">
            <button
              className="profile-card__action profile-card__action--primary"
              disabled={busy || !connection}
              onClick={() => connection && onMessage?.(connection.id, card)}
            >
              Message
            </button>
            {/* "Let's match" invite (Phase D). InviteButton self-gates on
                social.invites_feed and self-hides a game_join with no room. */}
            {invitesEnabled && <InviteButton toProfileId={profileId} type="lets_match" />}
          </div>
        );
      case 'blocked':
        return <span className="profile-card__blocked">Blocked</span>;
      default:
        return null;
    }
  };

  return (
    <div className="profile-card">
      {onClose && (
        <button className="profile-card__close" aria-label="Close" onClick={onClose}>
          ×
        </button>
      )}

      <div className="profile-card__header">
        <div
          className="profile-card__avatar"
          style={card.avatarUrl ? { backgroundImage: `url(${card.avatarUrl})` } : undefined}
        >
          {/* Character demoted to a corner chip (V2 Amethyst WU-02, R3) — the
              photo leads; "playing as" is a small badge, not its own row. */}
          {card.activeCharacter && (
            <span
              className="profile-card__character-chip"
              title={`Playing as ${card.activeCharacter.name} (${card.activeCharacter.characterType})`}
            >
              🎮
            </span>
          )}
        </div>
        <div className="profile-card__identity">
          <h3 className="profile-card__name">{card.displayName}</h3>
          {(() => {
            const shared = sharedInterestLine(card, viewerInterestNames);
            return shared ? <p className="profile-card__shared-line">{shared}</p> : null;
          })()}
          {card.headline && <p className="profile-card__headline">{card.headline}</p>}
          {presenceEnabled && card.presence && (
            <PresenceBadge presence={card.presence} showLabel />
          )}
        </div>
      </div>

      {card.bio && <p className="profile-card__bio">{card.bio}</p>}

      {card.fields.length > 0 && (
        <dl className="profile-card__fields">
          {card.fields.map((f) => (
            <div className="profile-card__field" key={f.key}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="profile-card__actions">{renderAction()}</div>
    </div>
  );
}

export default ProfileCard;
